import { Component, OnInit, inject, ViewChild, ElementRef, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { signInWithRedirect } from 'aws-amplify/auth';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { CognitoService } from '../../core/service/cognito.service';
import { ACCESS_LEVEL, Config, SUBSCRIPTION_TIER } from '../../core/config/config'
import { UserService } from 'src/app/core/service/user.service';
import { ToastService } from 'src/app/toast/toast.service';
import { IUser } from 'src/app/core/models/user.model';
import { StripeService } from 'src/app/stripe/stripe.services';
import { AuthStorageService } from '../../core/service/auth-storage.service';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'app-sign-up',
  templateUrl: 'sign-up.component.html',
  styleUrls: ['./sign-up.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class SignUpComponent implements OnInit {
  @ViewChild('otpInput') otpInput!: ElementRef;
  
  currentStep: number = 1;
  loading: boolean;
  isConfirm: boolean;
  user: IUser;
  otpConfirmed: boolean = false;
  showPassword: boolean = false;
  currentSlide: number = 0;
  
  signupForm: FormGroup;
  companyForm: FormGroup;
  referralForm: FormGroup;
  
  seniorityLevels = [
    'C-Level / Founder',
    'VP / Director',
    'Head of Department / Team Lead',
    'Specialist / Associate',
    'Other'
  ];

  referralSources = [
    'Google',
    'Reddit',
    'LinkedIn',
    'ChatGPT',
    'Referral',
    'Events',
    'Others'
  ];

  accountParam: any;
  billParam: any;
  paymentRedirection: boolean = false;
  passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z0-9]).{8,}$/;

  private router = inject(Router);
  private cognitoService = inject(CognitoService);
  private formBuilder = inject(FormBuilder);
  private userService = inject(UserService);
  private toastService = inject(ToastService);
  private stripeService = inject(StripeService);
  private route = inject(ActivatedRoute);
  private authStorage = inject(AuthStorageService);

  constructor() {
    this.loading = false;
    this.isConfirm = false;
    this.user = {} as IUser;
    
    this.signupForm = this.formBuilder.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.pattern(this.passwordRegex)]],
      termsAccepted: [false, Validators.requiredTrue],
      marketingConsent: [false]
    });

    this.companyForm = this.formBuilder.group({
      organization: [''],
      role: [''],
      referralSource: ['']
    });

    this.referralForm = this.formBuilder.group({
      referralSource: ['']
    });
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.accountParam = params['a'];
      this.billParam = params['bill'];
      if(this.accountParam){
        const accountId = parseInt(this.accountParam.split('t')[1], 10);
        const account = Config.USER_ACCOUNTS.find(account => account.id === accountId);
        if (account && (account.id === 4 || account.id === 5)) {
          return this.paymentRedirection = true;
        }
      }
      return this.paymentRedirection = false;
    });

    this.startSlideRotation();
  }

  startSlideRotation() {
    setInterval(() => {
      this.currentSlide = (this.currentSlide + 1) % 3;
    }, 4000);
  }

  nextStep() {
    if (this.currentStep < 3) {
      this.currentStep++;
    }
  }

  previousStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  skipStep() {
    this.nextStep();
  }

  async startSignup() {
    if (this.signupForm.valid) {
      this.loading = true;
      const formData = this.signupForm.value;
      
      this.user.email = formData.email;
      this.user.password = formData.password;
      this.user.firstName = formData.firstName;
      this.user.lastName = formData.lastName;
      this.user.name = `${formData.firstName} ${formData.lastName}`;

      try {
        const signUpResult = await this.cognitoService.signUp(this.user);
        this.user.sub = signUpResult.userId;
        this.isConfirm = true;
        this.nextStep();
      } catch (err: any) {
        if (err.code === 'UsernameExistsException') {
          await this.handleUnconfirmedUser();
        } else {
          this.toastService.showError('Error', err.message);
        }
      } finally {
        this.loading = false;
      }
    }
  }

  async confirmOTP(otp: string) {
    this.loading = true;
    try {
      await this.cognitoService.confirmSignUp({
        ...this.user,
        code: otp
      });
      this.otpConfirmed = true;
      this.nextStep();
    } catch (err: any) {
      this.toastService.showError('Error', err.message);
    } finally {
      this.loading = false;
    }
  }

  async finalizeSignup() {
    this.loading = true;
    const companyData = this.companyForm.value;

    if (companyData.organization) {
      this.user.organization = companyData.organization;
    }
    if (companyData.role) {
      this.user.role = companyData.role;
    }
    if (companyData.referralSource) {
      this.user.referralSource = companyData.referralSource;
    }
    this.user.status = 'active';
    
    try {
      await lastValueFrom(this.userService.createUser(this.user));
      
      const result = await this.cognitoService.signIn(this.user);
      
      if (result.isSignedIn && result.nextStep.signInStep === 'DONE') {
        const userData = result.user || {};
        const loginResponse = await lastValueFrom(this.userService.loginUser());
        
        const mergedData = { ...userData, ...(loginResponse || {}) };
        this.authStorage.setItem('user', mergedData);
        this.user = mergedData;

        await lastValueFrom(this.updateUser());
        
        if (this.paymentRedirection) {
          await this.stripeService.directRedirection(this.billParam, this.accountParam);
        } else {
          await this.router.navigate(['/'], { replaceUrl: true });
          window.location.reload();
        }
      }
    } catch (err: any) {
      this.toastService.showError('Error', err.message);
    } finally {
      this.loading = false;
    }
  }

  handleUnconfirmedUser() {
    return this.cognitoService.resendCode(this.user.email)
      .then((res: any) => {
        this.isConfirm = true;
        this.nextStep();
      });
  }

  updateUser() {
    return this.userService.updateUser({
      role: this.user.role,
      team: this.user.team,
      organization: this.user?.organization,
      referralSource: this.user?.referralSource
    });
  }

 

  saveTeam(data: any) {
    this.user.team = data.name;
  }

  saveRole(data: any) {
    this.user.role = data.name;
  }

  passwordValidator() {
    if (!this.passwordRegex.test(this.signupForm.value.password)) {
      return true;
    }
    return false;
  }
}
